import * as React from "react";
import { MultiSelect, IOption } from "../../atoms/forms/MultiSelect";
import { IApplicationState } from "../../../store";
import * as Problems from "../../../store/problems";
import { FetchState } from "../../../entities/FetchState";
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { IData as ProblemData } from '../../../store/problems/problemsList';
import { IStoreMultiSelectControls } from "./ICommonInputsControls";

// Problem interface from the store
type Problem = ProblemData;


interface IProblem extends IOption {
    problem: Problem;
}

const mapProblemToValue = (problem: Problem): IProblem => {
    return {
        value: problem.id,
        label: problem.name,
        problem: problem
    }
}

// Props interface
interface IProps {
    selectedProblemIds?: string[];
}

// Actual Component
export type AllProps = IProps & IStoreMultiSelectControls<Problem>;
export const ProblemMultiSelect = (props: AllProps) => {
    const { t: tDatatypes } = useTranslation('datatypes');
    const { t: tCommon } = useTranslation('common');
    
    // Get Redux state and dispatch
    const problemsState = useSelector((state: IApplicationState) => state.problems.problemsList);
    const dispatch = useDispatch();

    // Internal state for selected problems - initialize with preselected problems if available
    const [selectedProblems, setSelectedProblems] = React.useState<IProblem[]>(() => {
        // Try to initialize with preselected problems if data is already available
        if (problemsState.ProblemsList.data && props.selectedProblemIds) {
            const problems = problemsState.ProblemsList.data
                .filter(p => props.selectedProblemIds!.includes(p.id))
                .map(mapProblemToValue);
            return problems;
        }
        // If using value from common interface
        else if (props.value) {
            return props.value.map(mapProblemToValue);
        }
        return [];
    });

    // Fetch problems on initial render if not loaded or if the data array is empty
    React.useEffect(() => {
        const shouldFetch = 
            problemsState.ProblemsList.state === FetchState.NotFetched || 
            (problemsState.ProblemsList.state === FetchState.Success && 
             (!problemsState.ProblemsList.data || problemsState.ProblemsList.data.length === 0));
             
        if (shouldFetch) {
            dispatch(Problems.ActionCreators.problemsList.FetchData());
        }
    }, [dispatch, problemsState.ProblemsList.state, problemsState.ProblemsList.data]);

    // Update selected problems when data is loaded or selectedProblemIds prop changes
    React.useEffect(() => {
        if (problemsState.ProblemsList.data) {
            if (props.selectedProblemIds) {
                const problems = problemsState.ProblemsList.data
                    .filter(p => props.selectedProblemIds!.includes(p.id))
                    .map(mapProblemToValue);
                setSelectedProblems(problems);
            } else if (props.value) {
                // Handle value from common interface
                setSelectedProblems(props.value.map(mapProblemToValue));
            } else {
                setSelectedProblems([]);
            }
        }
    }, [problemsState.ProblemsList.data, props.selectedProblemIds, props.value, problemsState.ProblemsList.state]);

    // Map problems to select options
    const problems = React.useMemo(() => {
        return (problemsState.ProblemsList.data || [])
            .map(mapProblemToValue)
            .sort((a, b) => {
                if (a.label && b.label) {
                    return a.label.localeCompare(b.label);
                }
                // Fallback: compare by id if label is undefined
                return String(a.value).localeCompare(String(b.value));
            });
    }, [problemsState.ProblemsList.data, problemsState.ProblemsList.state]);

    // Force component re-render when problems list changes state from loading to success
    const [forceUpdateKey, setForceUpdateKey] = React.useState(0);
    React.useEffect(() => {
        if (problemsState.ProblemsList.state === FetchState.Success && problemsState.ProblemsList.data && problemsState.ProblemsList.data.length > 0) {
            setForceUpdateKey(prev => prev + 1);
        }
    }, [problemsState.ProblemsList.state, problemsState.ProblemsList.data]);

    const updateProblems = (problems: IProblem[] | null) => {
        const problemArray = problems || [];
        setSelectedProblems(problemArray);
        
        // Call onChange callback from common interface
        if (props.onChange) {
            const problemData = problemArray.map(p => p.problem);
            props.onChange(problemData.length > 0 ? problemData : null, !props.required || problemArray.length > 0);
        }
    }

    const isDisabled = props.disabled || problemsState.ProblemsList.state !== FetchState.Success;
    const isLoading = problemsState.ProblemsList.state === FetchState.Busy || problemsState.ProblemsList.state === FetchState.NotFetched || props.loading;
    const error = props.error || (problemsState.ProblemsList.state === FetchState.Error ? [true, tCommon('errorLoadingData')] : undefined);

    return (<MultiSelect 
        key={forceUpdateKey}
        label={(props.label || tDatatypes('problemLabel') || "Problems") + (problemsState.ProblemsList.state !== FetchState.Success ? tCommon('loading') : "")}
        placeholder={props.placeholder || (tDatatypes('problemPlaceholder') || "Select problems...")}
        options={problems}
        value={selectedProblems}
        disabled={isDisabled}
        onChange={updateProblems}
        loading={isLoading}
        error={error}
        required={props.required}
        prevalidate={props.prevalidate}
        isUnknownValuesEnabled={props.isUnknownValuesEnabled}
    />)
}

